import Styles from './NodeTimeline.module.scss';
import { Avatar, Typography, Paper } from '@mui/material';
import { CircularProgressWithLabel } from 'components/global/ChallengeLayout/CircularProgress';
import React from 'react';
import Timeline from '@mui/lab/Timeline';
import { timelineItemClasses, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent } from '@mui/lab';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import PlayArrow from '@mui/icons-material/PlayArrow';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import LockIcon from '@mui/icons-material/Lock';
import { useContext } from 'react';
import { ViewContext } from 'components/lib';

/**
 * Returns the scrolling timeline comp with step counter for Nodes
 * @param {number} highlightStep If no value is passed then the step with "current" is highlighted, else the index of the number passed
 * @param {boolean} hideToolTip If false, displays {step.command} for steps marked as prev
 * @param {boolean} nodeFinished if true sets the step counter to the last step
 * @param {number} currentStep the current step for which flag is set to current, used to calculate steps completed and %
 * @param {list} steps steps is a list of JSONs, with entries flag: {'current', 'next', 'prev'}, command, and timeline
 * @param {React.MutableRefObject} currentRef to autoscroll to this element when flag is changed, ref set at the view level on step change
 * @param {boolean} collapsable controls if timeline element is collapsable or not. Default is false
 * @returns {JSX.Element} React element showing a step counter and scrollable timeline with steps
 */
export function NodeTimeline({ hideToolTip = true, nodeFinished, currentStep, steps, currentRef, collapsable = false, setCurrentStep, maxUnlocked, onStepChange }) {
    const numOfSteps = steps.length;

    const viewContext = useContext(ViewContext);

    const HtmlTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: ' #292D3E',
            color: "#F8F8F8",
            fontSize: "14px",
            padding: "px",
            border: '1px solid #dadde9',
        },
    }));

    const stepIcons = {
        current: <PlayArrow fontSize="large" sx={{ color: "#4ade80" }} />,
        prev: <DoneAllIcon fontSize="large" sx={{ color: "#4ade80" }} />,
        next: <LockIcon fontSize="large" sx={{ color: "lightgrey" }} />
    };

    const getStepFlag = (index) => {
        if (index === maxUnlocked) return "current";    // current step
        if (index <= maxUnlocked) return "prev";        // completed and can navigate
        return "next";                                  // locked
    };

    const handleStepNavigation = (clickedIndex) => {
        if (clickedIndex > maxUnlocked || !setCurrentStep) return;
        // Only allow navigation if ≤ maxUnlocked
        setCurrentStep(clickedIndex);
        onStepChange?.(clickedIndex) //conditionally calling  if stepChange is passed.
    };

    const completedSteps = nodeFinished ? numOfSteps : Math.max(maxUnlocked ?? 0, currentStep);

    const completedText = viewContext.t(
        'Components.NodeLayout.NodeTimeline.completed',
        { currentStep: completedSteps, totalSteps: numOfSteps }
    );

    return (
        <div id='feedbackContainer' className={`${Styles.feedbackContainer} ${collapsable && Styles.expanded}`}>
            <div id='progressionContainer' className={Styles.progressionContainer}>
                <Paper
                    elevation={8}
                    sx={{
                        width: "100%",
                        margin: "4px",
                        borderRadius: "34px",
                        height: "fit-content",
                        display: "flex",
                        alignItems: "center",
                        padding: "2px",
                        flexDirection: "row",
                        backgroundColor: "#EEEEEE",
                        overflowX: 'hide',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis'
                    }}
                >
                    <Avatar sx={{
                        bgcolor: "#393E46",
                        width: 62,
                        height: 62,
                        alignSelf: "flex-start"
                    }}>
                        <CircularProgressWithLabel
                            value={!nodeFinished
                                ? Math.ceil((Math.max(maxUnlocked ?? 0, currentStep)) / numOfSteps * 100)
                                : 100}
                            progresscolour={"#16FF00"}
                            textcolour={"white"} />
                    </Avatar>
                    <div
                        id='progressTextContainer'
                        className={Styles.progreessTextContainer}
                    >
                        <Typography
                            // variant='h7'
                            sx={{
                                // marginLeft: '10px',
                                // marginRight: '10px',
                                color: "#393E46",
                            }}>
                            {completedText}
                        </Typography>
                    </div>
                </Paper>
            </div>
            <div id='timelineContainer' className={Styles.timelineContainer}>
                <div
                    id="timelinescrolldiv"
                    className={Styles.timelinescrolldiv}
                >
                    <Timeline
                        sx={{
                            height: '100%',
                            [`& .${timelineItemClasses.root}:before`]: {
                                flex: 0,
                                padding: 0,
                            },
                        }}
                    >
                        {steps.map((label, index) => {
                            let flag = getStepFlag(index);
                            const isHighlighted = index === currentStep;
                            return (
                                <React.Fragment key={`li-nodeTimeline-${index}`}>
                                    <TimelineItem
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            '&:hover': {
                                                cursor: 'pointer'
                                            },
                                        }}
                                        ref={flag === "current" ? currentRef : null}
                                        id={flag === "current" ? "currenttimelineHolder" : `timelineHolder-${index}`}
                                        onClick={() => handleStepNavigation(index)}
                                    >
                                        <TimelineSeparator >
                                            {stepIcons[flag]}
                                            {steps.length - 1 !== index && <TimelineConnector />}
                                        </TimelineSeparator>

                                        <TimelineContent sx={{ flex: "1" }}>
                                            <HtmlTooltip
                                                title={label.command}
                                                placement='left'
                                                disableHoverListener={hideToolTip || (flag === "current" || flag === 'next')}
                                            >
                                                <Paper
                                                    // elevation={8}
                                                    sx={{
                                                        // margin: "8px",
                                                        flexDirection: "row",
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        boxShadow: "none",
                                                        fontWeight: isHighlighted ? 'bold' : 'normal',
                                                        color: "darkslategray",
                                                        backgroundColor: 'transparent',
                                                        fontFamily: '"Ubuntu Mono", monospace;'
                                                    }}
                                                >
                                                    {label.timeline}
                                                </Paper>
                                            </HtmlTooltip>
                                        </TimelineContent>
                                    </TimelineItem>
                                </React.Fragment>
                            );
                        })}
                    </Timeline>
                </div>
            </div>
        </div >
    );
}
